import { JSONValue } from 'core/common/entities';
import {
  ExperimentDescription,
  ExperimentRawResult,
  FeatureDefinition,
  FeatureFlagsAttributes,
  FeatureFlagsConfig,
  FeatureRawResult,
  FeaturesPayload,
  FeatureValue,
  LoadFeaturesOptions,
} from '../entities';
import { FeatureFlags } from './FeatureFlags';

export class FeatureFlagsDecorator implements FeatureFlags {
  private featureFlags: FeatureFlags;

  constructor(featureFlags: FeatureFlags) {
    this.featureFlags = featureFlags;
  }

  async loadFeatures(
    options: LoadFeaturesOptions = {},
  ): Promise<{ [x: string]: FeatureDefinition<JSONValue> } | undefined> {
    return this.featureFlags.loadFeatures(options);
  }

  getSplitId(): string {
    return this.featureFlags.getSplitId();
  }

  getFeatures(): FeatureFlagsConfig['features'] {
    return this.featureFlags.getFeatures();
  }

  getPayload(): FeaturesPayload {
    return this.featureFlags.getPayload();
  }

  setForcedFeatureValues(featureValues: Record<string, unknown>) {
    this.featureFlags.setForcedFeatureValues(featureValues);
  }

  getAttributes(): FeatureFlagsAttributes {
    return this.featureFlags.getAttributes();
  }

  getExperimentGroup<T extends FeatureValue>(experimentName: string): T | null {
    return this.featureFlags.getExperimentGroup(experimentName);
  }

  getFeatureRawResult<T extends FeatureValue>(featureName: string): FeatureRawResult<T | null> {
    return this.featureFlags.getFeatureRawResult(featureName);
  }

  getFeatureValue<T extends FeatureValue = FeatureValue>(featureName: string, defaultValue?: T): T {
    return this.featureFlags.getFeatureValue(featureName, defaultValue);
  }

  isFeatureOff(featureName: string): boolean {
    return this.featureFlags.isFeatureOff(featureName);
  }

  isFeatureOn(featureName: string): boolean {
    return this.featureFlags.isFeatureOn(featureName);
  }

  async setAttributes(attributes: FeatureFlagsAttributes): Promise<void> {
    await this.featureFlags.setAttributes(attributes);
  }

  setStateUpdateHandler(callback: () => void): (() => void) | void {
    return this.featureFlags.setStateUpdateHandler(callback);
  }

  setIdentifier(identifier: string): void {
    this.featureFlags.setIdentifier(identifier);
  }

  onTrackExperiment<T extends FeatureValue = string>(
    callback: (experiment: ExperimentDescription<T>, result: ExperimentRawResult<T>) => void,
  ) {
    this.featureFlags.onTrackExperiment<T>(callback);
  }

  onAttributesUpdated(callback: (attributes: FeatureFlagsAttributes) => void): void {
    this.featureFlags.onAttributesUpdated(callback);
  }

  destroy(): void {
    this.featureFlags.destroy();
  }
}
